import { Handle, Position, NodeResizer } from 'reactflow';
import { Split, Loader2 } from 'lucide-react';

interface ConditionalSplitNodeProps {
    data: any;
    selected: boolean;
}

export default function ConditionalSplitNode({ data, selected }: ConditionalSplitNodeProps) {
    const conditions = data.conditions || [];

    // Total number of output handles: one per condition + one default
    const totalHandles = conditions.length + 1;

    // Calculate the top position (as a percentage) for each handle so that
    // ReactFlow can correctly determine where edges attach.
    // We distribute them evenly between py-2 (8px) padding top and bottom.
    const getHandleTopPercent = (index: number) => {
        // Map index 0..totalHandles-1 evenly into the range [8px, height-8px]
        // Using CSS percentage relative to the container height
        const fraction = (index + 1) / (totalHandles + 1);
        return `${fraction * 100}%`;
    };

    return (
        <div className="relative flex flex-col items-center">
            <NodeResizer
                color="#94a3b8"
                isVisible={selected}
                minWidth={48}
                minHeight={48}
                handleStyle={{ width: 8, height: 8, borderRadius: 2 }}
            />

            {/* Main Node Body */}
            <div
                className={`
                    relative group flex flex-col items-center justify-center p-2
                    min-w-[80px] rounded-xl shadow-sm border-[1.5px] transition-all duration-300 z-10 bg-white
                    ${selected ? 'border-yellow-400 ring-4 ring-yellow-400/10 shadow-xl scale-105' : 'border-orange-300 bg-orange-50 hover:border-orange-400 hover:shadow-md'}
                `}
                style={{ minHeight: Math.max(60, totalHandles * 24 + 20) }}
            >
                <Split className="h-6 w-6 text-orange-500 mb-1 transition-opacity duration-300" strokeWidth={1.5} />
                {data?.executionStatus === 'Running' && (
                    <div className="absolute inset-0 flex items-center justify-center bg-white/60 rounded-xl z-20">
                        <Loader2 className="h-5 w-5 text-orange-600 animate-spin" />
                    </div>
                )}
                <span className="text-[9px] font-bold text-slate-500 uppercase tracking-wider text-center max-w-[90px] leading-tight">
                    {data.label || 'Split'}
                </span>

                {/* Input Handle (Left) */}
                <Handle
                    type="target"
                    position={Position.Left}
                    id="in"
                    className="!w-3 !h-3 !bg-white !border-2 !border-slate-400 z-50 pointer-events-auto"
                    style={{ left: -6, top: '50%' }}
                />

                {/* Output Handles for each condition — absolutely positioned so ReactFlow
                    can correctly compute edge attachment points via getBoundingClientRect */}
                {conditions.map((cond: any, idx: number) => (
                    <div
                        key={cond.id}
                        className="absolute flex items-center pointer-events-none"
                        style={{ right: 0, top: getHandleTopPercent(idx), transform: 'translateY(-50%)' }}
                    >
                        {/* Label shown on hover */}
                        <span className="absolute right-5 text-[8px] text-gray-400 whitespace-nowrap bg-white px-1 border rounded shadow-sm mr-1 pointer-events-none opacity-0 group-hover:opacity-100 transition-opacity">
                            {cond.outputLabel || cond.Name || cond.name || `Branch ${idx + 1}`}
                        </span>
                        {/* Stats Badge */}
                        {data.outputStats && data.outputStats[`condition-${cond.id}`] !== undefined && (
                            <span className="absolute left-full ml-4 text-[8px] font-mono text-xs bg-slate-100/80 text-slate-600 px-1 rounded border border-slate-200 whitespace-nowrap z-50">
                                {data.outputStats[`condition-${cond.id}`].toLocaleString()} rows
                            </span>
                        )}
                        <Handle
                            type="source"
                            position={Position.Right}
                            id={`condition-${cond.id}`}
                            style={{ right: -6, position: 'absolute', top: '50%', transform: 'translateY(-50%)' }}
                            className="!w-3 !h-3 !bg-white !border-2 !border-orange-500 z-50 cursor-pointer pointer-events-auto"
                        />
                    </div>
                ))}

                {/* Default Handle */}
                <div
                    className="absolute flex items-center pointer-events-none"
                    style={{ right: 0, top: getHandleTopPercent(conditions.length), transform: 'translateY(-50%)' }}
                >
                    <span className="absolute right-5 text-[8px] text-gray-400 whitespace-nowrap bg-white px-1 border rounded shadow-sm mr-1 pointer-events-none opacity-0 group-hover:opacity-100 transition-opacity">
                        Default
                    </span>
                    {/* Stats Badge for Default */}
                    {data.outputStats && data.outputStats['default'] !== undefined && (
                        <span className="absolute left-full ml-4 text-[8px] font-mono text-xs bg-slate-100/80 text-slate-600 px-1 rounded border border-slate-200 whitespace-nowrap z-50">
                            {data.outputStats['default'].toLocaleString()} rows
                        </span>
                    )}
                    <Handle
                        type="source"
                        position={Position.Right}
                        id="default"
                        style={{ right: -6, position: 'absolute', top: '50%', transform: 'translateY(-50%)' }}
                        className="!w-3 !h-3 !bg-white !border-2 !border-slate-400 z-50 cursor-pointer pointer-events-auto"
                    />
                </div>
            </div>
        </div>
    );
}
